<script setup lang="ts">
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import {
  NXGDSNavigationPanel,
  NXGDSNavigationItem,
  NXGDSTagIndicator,
  NXGDSModal,
} from '@nsftx/seven-nxg-design-system'
import { useIntegrator, useIntegratorStore } from '@nsftx/seven-client-retail-vue-integrator'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { useDmCoreSpaceOperatorAuthStore } from '@nsftx/seven-client-retail-vue-dm-core'
import { logService } from '@nsftx/seven-client-retail-utils'
import { useNotification } from '@nsftx/seven-client-retail-vue-sgp'
import errorParser from '@/utils/errorParser'
import { useHeaderStore } from '@/stores/header/headerStore'
import { useMainMenu } from '@/composables/useMainMenu/useMainMenu'
import SSMainMenuModalFooter from './SSMainMenuModalFooter.vue'

const LOG_PREFIX = '[SSMainMenuModal]'
const MAIN_MENU_MODAL_WIDTH = '48.75rem'

const router = useRouter()
const { gamesItems, operationItems, navigateToGame, navigateToOperation } = useMainMenu()
const { logoutOperator } = useDmCoreSpaceOperatorAuthStore()
const { isMainMenuShown } = storeToRefs(useHeaderStore())
const { disconnectFromNotificationService } = useNotification()
const { t } = useI18n({ useScope: 'global' })

const handleLogout = async () => {
  const { teardownIntegrator } = useIntegrator()
  const { removeAllIframes } = useIntegratorStore()
  const { showNotification, closeNotificationById } = useNotificationsStore()

  try {
    await logoutOperator()
    removeAllIframes()
    teardownIntegrator()
    disconnectFromNotificationService()

    await router.replace({ name: 'login' })
    closeNotificationById('operatorLogin')
    showNotification({
      id: 'operatorLogout',
      text: [t('operator_logout')],
      variant: 'warning',
    })
  } catch (error) {
    logService.error(`${LOG_PREFIX} Operator logout failed.`, {
      code: 'SS_OPERATOR_LOGOUT_ERROR',
      ...errorParser.parseUpstream(error),
    })
  }
}
</script>

<template>
  <NXGDSModal
    v-model="isMainMenuShown"
    :title="$t('common_menu')"
    class="ss-main-menu-modal"
    data-testid="ss-main-menu"
    close-on-outside-click
    show-close-button
    gap="28px"
    :width="MAIN_MENU_MODAL_WIDTH"
  >
    <div class="ss-main-menu-modal__container">
      <div v-if="operationItems.length" class="ss-main-menu-modal__container__operations">
        <NXGDSNavigationPanel
          :model-value="operationItems"
          grid="auto / repeat(2, 1fr)"
          row-gap="var(--nxgds-alias-spacing-xs)"
          column-gap="var(--nxgds-alias-spacing-xs)"
          @on:item-click="navigateToOperation"
        >
          <template #item="{ item, handleClick }">
            <NXGDSNavigationItem
              :title="item.title"
              :state="item.state"
              size="lg"
              :icon-name="item.iconName"
              tabindex="0"
              @click="handleClick"
              @keydown.enter.prevent="handleClick"
              @keydown.space.prevent="handleClick"
            >
              <template #tags v-if="item.tags?.length">
                <NXGDSTagIndicator
                  v-for="(tag, index) in item.tags"
                  :key="index"
                  :label="tag.label"
                  :variant="tag.variant"
                />
              </template>
            </NXGDSNavigationItem>
          </template>
        </NXGDSNavigationPanel>
      </div>

      <div
        v-if="operationItems.length && gamesItems.length"
        class="ss-main-menu-modal__container__divider"
      />

      <div v-if="gamesItems.length" class="ss-main-menu-modal__container__games">
        <NXGDSNavigationPanel
          :model-value="gamesItems"
          grid="auto / repeat(2, 1fr)"
          row-gap="var(--nxgds-alias-spacing-xs)"
          column-gap="var(--nxgds-alias-spacing-xs)"
          @on:item-click="navigateToGame"
        >
          <template #item="{ item, handleClick }">
            <NXGDSNavigationItem
              :title="item.title"
              :state="item.state"
              size="lg"
              :icon-name="item.iconName"
              tabindex="0"
              @click="handleClick"
              @keydown.enter.prevent="handleClick"
              @keydown.space.prevent="handleClick"
            >
              <template #tags v-if="item.tags?.length">
                <NXGDSTagIndicator
                  v-for="(tag, index) in item.tags"
                  :key="index"
                  :label="tag.label"
                  :variant="tag.variant"
                />
              </template>
            </NXGDSNavigationItem>
          </template>
        </NXGDSNavigationPanel>
      </div>
    </div>
    <template #footer>
      <SSMainMenuModalFooter @click-logout="handleLogout" />
    </template>
  </NXGDSModal>
</template>

<style lang="scss">
@use '@/styles/mixins' as mixins;

.ss-main-menu-modal {
  --ss-main-menu-modal-height-md: 35rem;
  --ss-main-menu-modal-height-lg: 62.5rem;
  z-index: 2;

  &__container {
    display: flex;
    flex-direction: column;
    gap: var(--nxgds-alias-spacing-lg);
    padding-right: var(--nxgds-alias-spacing-2xs);
    margin-bottom: var(--nxgds-alias-spacing-lg);
    max-height: var(--ss-main-menu-modal-height-md);
    overflow-y: auto;
    scrollbar-color: rgba(255, 255, 255, 0.1) transparent;

    &::-webkit-scrollbar {
      background-color: rgba(255, 255, 255, 0.1);
    }

    &__divider {
      width: 100%;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
  }

  @media (min-height: 56.25rem) {
    &__container {
      max-height: var(--ss-main-menu-modal-height-lg);
    }
  }
}
</style>
